import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import UserService from '../services/UserService';
import { loadState, saveState } from 'src/lib/utils';
import { LOCAL_STORAGE_KEYS } from 'src/lib/constants';
import {
  clearOtpChallengeStorage,
  isOtpRequiredLoginPayload,
  persistOtpChallenge,
} from 'src/lib/auth/otpSession';
import { getLoginDeviceInfo } from 'src/lib/auth/deviceTracking';

const OTP_MESSAGES_MM = {
  required: 'လုံခြုံရေးအတည်ပြုရန် OTP ကုဒ် လိုအပ်ပါသည်။',
  sent: 'OTP ကုဒ်ကို သတ်မှတ်ထားသော Email သို့ ပို့ပြီးပါပြီ။',
  verifyFailed: 'OTP ကုဒ် မမှန်ပါ။ ထပ်မံစစ်ဆေးပြီး ကြိုးစားပါ။',
  verifyFailedReLogin: 'အတည်ပြုခြင်း မအောင်မြင်ပါ။ ကျေးဇူးပြု၍ Login ပြန်ဝင်ပြီး ထပ်မံကြိုးစားပါ။',
  resent: 'OTP အသစ်ကို Email သို့ ပြန်လည်ပို့ပြီးပါပြီ။',
  operationalFailure: 'OTP ပို့ဆောင်မှု မအောင်မြင်ပါ။ ကျေးဇူးပြု၍ ခဏအကြာတွင် ထပ်မံကြိုးစားပါ သို့မဟုတ် Administrator ကို ဆက်သွယ်ပါ။',
};

function normalizeOtpErrorMessage(message) {
  const text = String(message || '');

  if (text === 'Unable to complete sign-in. Please try again later.') {
    return OTP_MESSAGES_MM.operationalFailure;
  }

  if (text === 'Verification failed. Please sign in again.') {
    return OTP_MESSAGES_MM.verifyFailedReLogin;
  }

  return message;
}

const initialState = {
  user: null,
  datas: [],
  roles: [],
  formError: [],
  error: null,
  status: '',
  dataLoading: false,
  submitLoading: false,
  pagination: {
    page: null,
    perPage: null,
    total: null,
  },
  isFresh: false,
  isPendingUserDetails: false,
  isOpenUserDialog: false,
  loginSuccess: false,
  loginLoading: false,
  listLoading: false,
  requiresOtp: false,
  otpChallengeId: null,
  otpExpiresAt: null,
  otpResendLoading: false,
};
// ...


export const login = createAsyncThunk('user/login', async (data) => {
  try {
    const resp = await UserService.Login(data);
    if (isOtpRequiredLoginPayload(resp)) {
      persistOtpChallenge(resp.data.data.challenge_id, resp.data.data.expires_at);
    }
    return resp;
  } catch (e) {
    return e.response;
  }
});

export const verifyLoginOtp = createAsyncThunk('user/verifyLoginOtp', async (data) => {
  try {
    const resp = await UserService.VerifyLoginOtp({
      ...data,
      ...getLoginDeviceInfo(),
    });
    return resp;
  } catch (e) {
    return e.response;
  }
});

export const resendLoginOtp = createAsyncThunk('user/resendLoginOtp', async (data) => {
  try {
    const resp = await UserService.ResendLoginOtp(data);
    if (resp?.status === 200 && resp?.data?.data?.challenge_id) {
      persistOtpChallenge(resp.data.data.challenge_id, resp.data.data.expires_at);
    }
    return resp;
  } catch (e) {
    return e.response;
  }
});

export const logout = createAsyncThunk('user/logout', async () => {
  try {
    const resp = await UserService.Logout();
    return resp;
  } catch (e) {
    return e.response;
  }
});

export const userList = createAsyncThunk('user/list', async ({ perPage, page, keyword }) => {
  try {
    const resp = await UserService.List({ perPage, page, keyword });
    return resp;
  } catch (e) {
    return e.response;
  }
});

export const createUser = createAsyncThunk('user/createUser', async (data, { rejectWithValue }) => {
  try {
    const resp = await UserService.Create(data);
    return resp;
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

export const fetchUserDetails = createAsyncThunk('user/fetchUserDetails', async (data, { rejectWithValue }) => {
  try {
    const resp = await UserService.UserDetails(data);
    return resp;
  } catch (error) {
    return rejectWithValue(error?.response?.data);
  }
});

export const updateUser = createAsyncThunk('user/updateUser', async (data, { getState, dispatch, rejectWithValue }) => {
  try {
    const resp = await UserService.Put(data);
    const currentUser = getState()?.user?.user;
    const updatedId = resp?.data?.data?.id;
    // If editing the logged-in user, refresh Spatie permission payload (do not use incomplete update response).
    if (currentUser?.id && updatedId && Number(currentUser.id) === Number(updatedId)) {
      await dispatch(fetchUserDetails(currentUser.id));
    }
    return resp;
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

export const deleteUser = createAsyncThunk('user/deleteUser', async (data, { rejectWithValue }) => {
  try {
    const resp = await UserService.DeleteUser(data);
    return resp;
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

const userSlice = createSlice({
  name: 'user',
  initialState: initialState,
  reducers: {
    openUserDialog: (state, action) => {
      state.isOpenUserDialog = action.payload;
    },
    resetOtpChallenge: (state) => {
      state.requiresOtp = false;
      state.otpChallengeId = null;
      state.otpExpiresAt = null;
      state.error = null;
      state.formError = [];
      clearOtpChallengeStorage();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state, action) => {
        state.submitLoading = true;
        state.loginSuccess = false;
      })
      .addCase(login.fulfilled, (state, action) => {
        if (isOtpRequiredLoginPayload(action.payload)) {
          persistOtpChallenge(
            action.payload.data.data.challenge_id,
            action.payload.data.data.expires_at,
          );
          state.requiresOtp = true;
          state.otpChallengeId = action.payload.data.data.challenge_id;
          state.otpExpiresAt = action.payload.data.data.expires_at || null;
          state.loginSuccess = false;
          state.user = null;
          state.error = '';
          state.formError = [];
          toast.info(OTP_MESSAGES_MM.required);
        } else if (action.payload?.status === 200) {
          state.requiresOtp = false;
          state.otpChallengeId = null;
          state.otpExpiresAt = null;
          clearOtpChallengeStorage();
          state.user = action.payload?.data?.data;
          state.error = '';
          state.isFresh = true; // Data is fresh from login
          toast.success(action.payload?.data.message);
          state.loginSuccess = true;
          saveState(LOCAL_STORAGE_KEYS.user, action.payload?.data?.data);
          saveState(LOCAL_STORAGE_KEYS.token, action.payload?.data?.data?.token);
        } else {
          state.loginSuccess = false;
          if (action.payload?.data?.errors) {
            state.formError = action.payload?.data?.errors;
            state.error = null;
          } else {
            state.formError = [];
            state.error = normalizeOtpErrorMessage(action.payload?.data?.message);
          }
        }
        state.submitLoading = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.error = action.payload?.data;
        state.dataLoading = false;
        state.loginSuccess = false;
      })
      .addCase(verifyLoginOtp.pending, (state) => {
        state.submitLoading = true;
      })
      .addCase(verifyLoginOtp.fulfilled, (state, action) => {
        if (action.payload?.status === 200 && action.payload?.data?.data?.token) {
          state.requiresOtp = false;
          state.otpChallengeId = null;
          state.otpExpiresAt = null;
          clearOtpChallengeStorage();
          state.user = action.payload?.data?.data;
          state.error = '';
          state.formError = [];
          state.isFresh = true;
          toast.success(action.payload?.data.message);
          state.loginSuccess = true;
          saveState(LOCAL_STORAGE_KEYS.user, action.payload?.data?.data);
          saveState(LOCAL_STORAGE_KEYS.token, action.payload?.data?.data?.token);
        } else {
          state.loginSuccess = false;
          if (action.payload?.data?.errors) {
            state.formError = action.payload?.data?.errors;
            state.error = null;
          } else {
            state.formError = [];
            state.error = OTP_MESSAGES_MM.verifyFailedReLogin;
          }
        }
        state.submitLoading = false;
      })
      .addCase(verifyLoginOtp.rejected, (state, action) => {
        state.submitLoading = false;
        state.loginSuccess = false;
        state.error = normalizeOtpErrorMessage(action.payload?.data?.message || action.payload?.data)
          || OTP_MESSAGES_MM.verifyFailed;
      })
      .addCase(resendLoginOtp.pending, (state) => {
        state.otpResendLoading = true;
      })
      .addCase(resendLoginOtp.fulfilled, (state, action) => {
        if (action.payload?.status === 200 && action.payload?.data?.data?.challenge_id) {
          state.otpChallengeId = action.payload.data.data.challenge_id;
          state.otpExpiresAt = action.payload.data.data.expires_at || state.otpExpiresAt;
          state.error = '';
          state.formError = [];
          toast.info(OTP_MESSAGES_MM.resent);
        } else if (action.payload?.data?.errors) {
          state.formError = action.payload?.data?.errors;
          state.error = null;
        } else {
          state.formError = [];
          state.error = normalizeOtpErrorMessage(action.payload?.data?.message)
            || OTP_MESSAGES_MM.operationalFailure;
        }
        state.otpResendLoading = false;
      })
      .addCase(resendLoginOtp.rejected, (state, action) => {
        state.otpResendLoading = false;
        state.error = normalizeOtpErrorMessage(action.payload?.data?.message || action.payload?.data)
          || OTP_MESSAGES_MM.operationalFailure;
      })
      .addCase(logout.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state, action) => {
        // Ensure sign-in screen doesn't show stale "session expired"/OTP UI
        // from previous auth attempts.
        state.loading = false;
        clearOtpChallengeStorage();
        Object.assign(state, initialState);
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        clearOtpChallengeStorage();
        Object.assign(state, initialState);
      })
      .addCase(userList.pending, (state, action) => {
        state.listLoading = true;
      })
      .addCase(userList.fulfilled, (state, action) => {
        state.datas = [...action.payload?.data.data?.userList];
        state.pagination = action.payload?.data.data?.pagination;
        state.listLoading = false;
      })
      .addCase(userList.rejected, (state, action) => {
        state.error = action.payload?.data;
        state.listLoading = false;
      })
      .addCase(createUser.pending, (state, action) => {
        state.submitLoading = true;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.status = '';
        state.datas.unshift(action.payload.data.data);
        state.info = action.payload?.data.message;
        state.status = 'achieve';
        toast.success(action.payload?.data.message);
        state.formError = action.payload?.data.errors;
        state.submitLoading = false;
      })
      .addCase(createUser.rejected, (state, action) => {
        const errMsg = action.payload?.message ?? 'Failed to create user.';
        state.submitLoading = false;
        toast.error(errMsg);
      })
      .addCase(updateUser.pending, (state, action) => {
        state.submitLoading = true;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.info = action.payload?.data.message;
        const index = state.datas.findIndex((client) => client.id === action.payload.data.data.id);
        state.datas[index] = {
          ...state[index],
          ...action.payload.data.data,
        };
        toast.success(action.payload?.data.message);
        state.submitLoading = false;
      })
      .addCase(updateUser.rejected, (state, action) => {
        const errmsg = action.payload?.message ?? 'Failed to update user.';
        state.submitLoading = false;
        toast.error(errmsg);
      })
      .addCase(deleteUser.pending, (state, action) => {
        state.submitLoading = true;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {

        const deletedId = action.meta.arg;
        state.datas = state.datas.filter(item => item.id !== deletedId);
        toast.success(action.payload?.data.message);
        state.submitLoading = false;
        state.isOpenUserDialog = false;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        const errMsg = action.payload?.message ?? 'Failed to delete user.';
        state.submitLoading = false;
        toast.error(errMsg);
      })
      .addCase(fetchUserDetails.pending, (state, action) => {
        state.isPendingUserDetails = true;
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.isPendingUserDetails = false;
        state.user = action.payload.data.data;
        state.isFresh = true;
        // Keep localStorage aligned so remount uses fresh Spatie permissions.
        if (action.payload?.data?.data) {
          const existing = loadState(LOCAL_STORAGE_KEYS.user) || {};
          saveState(LOCAL_STORAGE_KEYS.user, {
            ...existing,
            ...action.payload.data.data,
            token: existing.token || action.payload.data.data.token,
          });
        }
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.isPendingUserDetails = false;
        // Keep existing user/permissions (stale session) but do not mark refresh as fresh.
        state.isFresh = false;
      })
  },
});

const { reducer } = userSlice;

export const { openUserDialog, resetOtpChallenge } = userSlice.actions;

export const userSelector = (state) => state?.user;

export default reducer;
